import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rgbaToHex } from '../../core/color';
import type { RGBA, SelectionCombineMode } from '../../core/types';
import { colorRangePreview } from '../../engine/selectionOps';
import { applyColorRange, colorRangeImage } from '../../state/selectionActions';
import { useAppStore } from '../../state/store';
import { Checkbox, Modal, Select, SliderRow } from '../components/controls';

const PREVIEW_MAX = 320;

const MODES: { value: SelectionCombineMode; label: string }[] = [
  { value: 'replace', label: 'New selection' },
  { value: 'add', label: 'Add to selection' },
  { value: 'subtract', label: 'Subtract from selection' },
  { value: 'intersect', label: 'Intersect with selection' },
];

type View = 'image' | 'selection';

function pixelAt(
  pixels: Uint8Array,
  width: number,
  x: number,
  y: number,
): RGBA {
  const offset = (y * width + x) * 4;
  return {
    r: pixels[offset] / 255,
    g: pixels[offset + 1] / 255,
    b: pixels[offset + 2] / 255,
    a: pixels[offset + 3] / 255,
  };
}

export function ColorRangeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const setStatus = useAppStore((state) => state.setStatus);
  const image = useMemo(() => (open ? colorRangeImage() : null), [open]);

  const [samples, setSamples] = useState<RGBA[]>([]);
  const [fuzziness, setFuzziness] = useState(40);
  const [invert, setInvert] = useState(false);
  const [mode, setMode] = useState<SelectionCombineMode>('replace');
  const [view, setView] = useState<View>('selection');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The dialog body mounts in a portal one commit after this component renders,
  // so the effect has to wait for the canvas to report itself as attached.
  const [attached, setAttached] = useState(false);
  const attach = useCallback((node: HTMLCanvasElement | null) => {
    canvasRef.current = node;
    setAttached(node !== null);
  }, []);

  const preview = useMemo(() => {
    if (!image) return null;
    if (view === 'image') {
      return new ImageData(
        new Uint8ClampedArray(image.pixels.slice()),
        image.width,
        image.height,
      );
    }
    return colorRangePreview(image, samples, fuzziness / 100, invert);
  }, [image, view, samples, fuzziness, invert]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!attached || !canvas || !preview) return;
    canvas.width = preview.width;
    canvas.height = preview.height;
    canvas.getContext('2d')?.putImageData(preview, 0, 0);
  }, [attached, preview]);

  const apply = () => {
    if (samples.length === 0) {
      setStatus('Click the preview to pick a colour first');
      return;
    }
    applyColorRange(samples, fuzziness / 100, invert, mode);
    onClose();
  };

  const scale = image ? Math.min(1, PREVIEW_MAX / Math.max(image.width, image.height)) : 1;

  return (
    <Modal
      open={open}
      title="Color Range"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            Select
          </button>
        </>
      }
    >
      <p className="dialog__note">
        Click the preview to sample a colour, Shift+click to add more. White marks what will be
        selected.
      </p>

      <div className="colorrange">
        <canvas
          ref={attach}
          className="colorrange__preview"
          style={{
            width: image ? image.width * scale : undefined,
            height: image ? image.height * scale : undefined,
          }}
          onClick={(event) => {
            if (!image) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const x = Math.floor(((event.clientX - rect.left) / rect.width) * image.width);
            const y = Math.floor(((event.clientY - rect.top) / rect.height) * image.height);
            if (x < 0 || y < 0 || x >= image.width || y >= image.height) return;
            const sample = pixelAt(image.pixels, image.width, x, y);
            setSamples((current) => (event.shiftKey ? [...current, sample] : [sample]));
          }}
        />

        <div className="colorrange__samples">
          {samples.length === 0 && <span className="dialog__note">No colours sampled</span>}
          {samples.map((sample, index) => (
            <button
              key={`${rgbaToHex(sample)}-${index}`}
              type="button"
              className="swatch"
              title={`Remove ${rgbaToHex(sample)}`}
              style={{ background: rgbaToHex(sample) }}
              onClick={() => setSamples((current) => current.filter((_, i) => i !== index))}
            />
          ))}
        </div>
      </div>

      <SliderRow
        label="Fuzziness"
        value={fuzziness}
        min={0}
        max={100}
        onChange={setFuzziness}
      />

      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Mode
        </span>
        <Select value={mode} onChange={setMode} options={MODES} />
      </div>

      <div className="field">
        <Checkbox label="Invert" checked={invert} onChange={setInvert} />
        <Checkbox
          label="Show image"
          checked={view === 'image'}
          onChange={(shown) => setView(shown ? 'image' : 'selection')}
        />
      </div>
    </Modal>
  );
}
